import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Menu, X, Bell, LogIn, UserPlus, Home } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { Logout } from "@/hooks/useAxiosPrivate";
import useConfig from "@/hooks/useConfig";
import { useAuthState, notifyAuthChanged } from "@/v2/hooks/useAuthState";
import { filterNavItems, isAdminRole } from "@/v2/lib/nav-items";
import { Button } from "@/v2/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/v2/components/ui/avatar";
import { Badge } from "@/v2/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/v2/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader } from "@/v2/components/ui/sheet";
import { SidebarLegalFooter } from "@/v2/components/SidebarLegalFooter";
import { DesktopHeaderSearch } from "./DesktopHeaderSearch";
import { MobileHeaderSearch } from "./MobileHeaderSearch";
import { PaymentAlert } from "./PaymentAlert";
import {
  buildAvatarMenuItems,
  buildMobileAuthItems,
  buildNavItems,
  type NavConfigInput,
} from "./nav-config";
import { useNotificationsCount, useViewerRole, useUserChannels } from "./api";

/** "/spotlight", the part of the route that comes before its :id. */
const SPOTLIGHT_PATH_PREFIX = PageRoutes.PersonalChannel.split("/:")[0];

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const config = useConfig();
  const { isAuthenticated, user } = useAuthState();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isTeam = searchParams.get("is_team") === "true";
  const insiderEnabled = process.env.REACT_APP_INSIDER === "true";

  const roleQuery = useViewerRole({ enabled: isAuthenticated });
  const isAdmin = isAdminRole(roleQuery.data ?? null);

  const notificationsQuery = useNotificationsCount({
    enabled: isAuthenticated,
  });
  const unreadCount = notificationsQuery.data ?? 0;

  const channelsQuery = useUserChannels({
    enabled: isAuthenticated && insiderEnabled,
  });
  const isChannelCreated = (channelsQuery.data?.length ?? 0) > 0;
  const featureEnabled = process.env.REACT_APP_FEATURE === "true";

  const navConfig: NavConfigInput = useMemo(
    () => ({
      planpage: config?.planpage ?? false,
      personalChannel: config?.personalChannel ?? "",
      tenantName: config?.name ?? "",
      insiderEnabled,
      isTeam,
      adminRoleId: roleQuery.data ?? null,
      featureEnabled,
      isChannelCreated,
    }),
    [
      config,
      insiderEnabled,
      isTeam,
      roleQuery.data,
      featureEnabled,
      isChannelCreated,
    ],
  );

  const navItems = useMemo(
    () =>
      filterNavItems(buildNavItems(navConfig), {
        isAuthenticated,
        isAdmin,
      }),
    [navConfig, isAuthenticated, isAdmin],
  );

  const avatarMenuItems = useMemo(
    () => buildAvatarMenuItems(navConfig),
    [navConfig],
  );

  const mobileAuthItems = useMemo(
    () => buildMobileAuthItems(navConfig),
    [navConfig],
  );

  const handleLogout = useCallback(() => {
    Logout();
    notifyAuthChanged();
    navigate(PageRoutes.Login);
    setMobileOpen(false);
  }, [navigate]);

  const handleNavClick = useCallback(
    (route: string) => {
      navigate(route);
      setMobileOpen(false);
    },
    [navigate],
  );

  const isActive = useCallback(
    (route: string) => {
      if (route === PageRoutes.HomePage) return location.pathname === "/";
      // Spotlight routes carry dynamic ids, and the build wizard lives one
      // level further down, so match on the prefix rather than the exact
      // path. Derived from the route constant instead of written out, because
      // the literal that used to be here was missed when the paths were
      // renamed away from /personal-channel.
      if (route.startsWith(SPOTLIGHT_PATH_PREFIX)) {
        return location.pathname.startsWith(SPOTLIGHT_PATH_PREFIX);
      }
      // Keep Events tab selected when viewing On Demand (VOD) or watching a game
      if (route === PageRoutes.UpcomingEvent) {
        return (
          location.pathname.startsWith(PageRoutes.UpcomingEvent) ||
          location.pathname.startsWith(PageRoutes.OnDemand) ||
          location.pathname.startsWith("/watch/")
        );
      }
      return location.pathname.startsWith(route);
    },
    [location.pathname],
  );

  const userInitial = (user?.firstName || user?.email || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <>
      <header className="v2 shadow-none">
        <div className="v2-header-container">
          {/* Left side: Mobile Menu Trigger + Logo */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Mobile menu trigger */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="v2-mobile-menu-trigger flex shrink-0 size-9 items-center justify-center rounded-md text-foreground min-[1200px]:hidden"
              aria-label="Open menu"
            >
              <Menu className="size-5 shrink-0" />
            </button>

            {/* Logo */}
            <Link
              to={PageRoutes.HomePage}
              className="flex shrink-0 items-center"
            >
              {config?.headerLogo ? (
                <img
                  src={config.headerLogo}
                  alt={config?.name ?? "logo"}
                  className="v2-header-logo object-contain"
                />
              ) : (
                <span className="text-lg font-bold text-foreground">
                  {config?.mainName ?? "HTPTV"}
                </span>
              )}
            </Link>
          </div>

          {/* Desktop nav (Centered) */}
          <nav className="v2-desktop-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.key}
                  to={item.route}
                  className={`v2-nav-link ${isActive(item.route) ? "active" : ""}`}
                >
                  <Icon className="size-4 shrink-0" />
                  {item.key === "personal-channel"
                    ? item.label
                    : item.label.toUpperCase()}
                </Link>
              );
            })}
          </nav>

          {/* Right side: Search, Notifications, Avatar */}
          <div className="ml-auto flex flex-shrink-0 items-center gap-2">
            {/* Desktop Search box */}
            <DesktopHeaderSearch customer={config?.customer ?? ""} />

            {/* Mobile Search trigger & dropdown */}
            <MobileHeaderSearch customer={config?.customer ?? ""} />

            {isAuthenticated ? (
              <>
                {/* HTPTV-695: failed-payment recovery indicator. Shows only for a
                  subscription in a payment-problem state (past_due / unpaid /
                  incomplete); renders nothing otherwise. Was imported but never
                  mounted (dropped in a merge), so the icon never appeared. */}
                <PaymentAlert />
                {/* Notifications bell (Visible on desktop and mobile) */}
                <button
                  type="button"
                  onClick={() => navigate(PageRoutes.Alert)}
                  className="relative flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  aria-label="Notifications"
                >
                  <Bell className="size-5" />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px]"
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </Badge>
                  )}
                </button>

                {/* Avatar menu (Visible on desktop and mobile) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      style={{ outline: "none", boxShadow: "none" }}
                      className="flex size-9 items-center justify-center rounded-full transition-opacity hover:opacity-80 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0 focus-visible:!ring-0 focus:!ring-offset-0"
                      aria-label="User menu"
                    >
                      <Avatar className="size-8 rounded-full">
                        {user?.profileImage && (
                          <AvatarImage
                            src={user.profileImage}
                            alt={`${user.firstName || ""} ${user.lastName || ""}`}
                            className="object-cover"
                          />
                        )}
                        <AvatarFallback className="rounded-full bg-primary text-xs font-medium text-primary-foreground">
                          {userInitial}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="v2 z-[99999] w-52 p-2"
                  >
                    {avatarMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem
                          key={item.key}
                          style={{ outline: "none", boxShadow: "none" }}
                          className={`cursor-pointer py-2 px-3 mb-1 rounded-md transition-colors hover:bg-accent !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0 focus-visible:!ring-0 focus:!bg-accent ${
                            item.isLogout
                              ? "text-destructive hover:!bg-destructive/10 mb-0"
                              : ""
                          }`}
                          onClick={() => {
                            if (item.isLogout) {
                              handleLogout();
                            } else if (item.route) {
                              navigate(item.route);
                            }
                          }}
                        >
                          <Icon className="mr-2 size-5 text-current" />
                          <span className="font-medium text-[15px]">
                            {item.label}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              /* Unauthenticated desktop auth actions. The mobile menu trigger
                 is hidden at this breakpoint (`min-[1200px]:hidden`), so this
                 is the only way a logged-out desktop visitor reaches Sign In. */
              <div className="hidden min-[1200px]:flex items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(PageRoutes.Register)}
                  className={`px-1 ${isActive(PageRoutes.Register) ? "bg-accent" : ""}`}
                >
                  <UserPlus className="size-4" />
                  Create Account
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(PageRoutes.Login)}
                  className={`px-1 ${isActive(PageRoutes.Login) ? "bg-accent" : ""}`}
                >
                  <LogIn className="size-4" />
                  Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="v2 z-[99999] p-0 w-full max-w-full sm:w-full sm:max-w-full flex flex-col max-h-[90dvh] rounded-t-2xl"
        >
          <SheetHeader className="border-b px-4 py-3">
            <div className="flex w-full items-center justify-between">
              {/* Close button perfectly aligned with nav icons */}
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex size-[22px] items-center justify-center rounded-md text-muted-foreground hover:bg-accent outline-none focus:outline-none"
                aria-label="Close menu"
              >
                <X className="size-[22px] text-action" />
              </button>

              {/* Logo in center */}
              <div className="flex flex-1 justify-center">
                {config?.headerLogo ? (
                  <img
                    src={config.headerLogo}
                    alt={config?.name ?? "logo"}
                    className="max-h-[36px] max-w-[100px] object-contain"
                  />
                ) : (
                  <span className="text-base font-bold text-foreground">
                    {config?.mainName ?? "HTPTV"}
                  </span>
                )}
              </div>

              {/* Spacer for symmetry */}
              <div className="size-[22px]" />
            </div>
          </SheetHeader>

          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Top items (Scrollable if needed) */}
            <div className="flex-1 overflow-y-auto py-2">
              <nav className="flex flex-col gap-0 w-full">
                {/* HOME Nav Item */}
                <button
                  type="button"
                  onClick={() => handleNavClick(PageRoutes.HomePage)}
                  className={`v2-mobile-nav-item w-full ${location.pathname === "/" ? "active" : ""}`}
                >
                  <Home size={22} className="shrink-0" />
                  <span className="flex-1">HOME</span>
                </button>

                {/* Remaining main nav items (excluding support and admin) */}
                {navItems
                  .filter(
                    (item) => item.key !== "support" && item.key !== "admin",
                  )
                  .map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.route);
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavClick(item.route)}
                        className={`v2-mobile-nav-item w-full ${active ? "active" : ""}`}
                      >
                        <Icon size={22} className="shrink-0" />
                        <span className="flex-1 text-left uppercase">
                          {item.key === "personal-channel"
                            ? item.label
                            : item.label.toUpperCase()}
                        </span>
                      </button>
                    );
                  })}
              </nav>

              {/* Mobile auth items */}
              {isAuthenticated ? (
                <>
                  <nav className="flex flex-col gap-0 w-full">
                    {mobileAuthItems.loggedIn.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => {
                            if (item.isLogout) {
                              handleLogout();
                            } else {
                              handleNavClick(item.route);
                            }
                          }}
                          className={`v2-mobile-nav-item w-full ${item.isLogout ? "text-destructive" : ""}`}
                        >
                          <Icon size={22} className="shrink-0" />
                          <span className="flex-1 text-left uppercase">
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                </>
              ) : (
                <>
                  <nav className="flex flex-col gap-0 w-full">
                    {mobileAuthItems.loggedOut.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => handleNavClick(item.route)}
                          className="v2-mobile-nav-item w-full"
                        >
                          <Icon size={22} className="shrink-0" />
                          <span className="flex-1 text-left uppercase">
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                </>
              )}
            </div>

            {/* Bottom items (Support, Admin) anchored to bottom */}
            <div className="mt-auto shrink-0 pb-4">
              {/* Divider */}
              <div className="w-full px-3 py-1">
                <div className="w-full h-px bg-border" />
              </div>

              <nav className="flex flex-col gap-0 w-full">
                {/* SUPPORT Item */}
                {navItems
                  .filter((item) => item.key === "support")
                  .map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.route);
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavClick(item.route)}
                        className={`v2-mobile-nav-item v2-mobile-nav-item-muted w-full ${active ? "active" : ""}`}
                      >
                        <Icon size={22} className="shrink-0" />
                        <span className="flex-1 uppercase">{item.label}</span>
                      </button>
                    );
                  })}

                {/* ADMIN Item (if applicable) */}
                {navItems
                  .filter((item) => item.key === "admin")
                  .map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.route);
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => handleNavClick(item.route)}
                        className={`v2-mobile-nav-item w-full ${active ? "active" : ""}`}
                      >
                        <Icon size={22} className="shrink-0" />
                        <span className="flex-1 uppercase">{item.label}</span>
                      </button>
                    );
                  })}
              </nav>

              {/* Global menu is the only nav on mobile/tablet (the schedule
                  page's own left rail collapses into a drawer below the
                  lg breakpoint), so legal links live here for persistent
                  access regardless of which page is open. */}
              <div className="px-3 pt-3">
                <SidebarLegalFooter />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
